import React, { useState } from 'react';
import { useProcessing } from '../contexts/ProcessingContext';
import { useUser } from '../contexts/UserContext';
import AIInsights from './AIInsights';
import './UserDashboard.css';

const SupportPanel = () => {
  const { user } = useUser();
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    firstName: '',
    lastName: '',
    subject: '',
    message: '',
    priority: 'normal',
    category: 'general',
    userEmail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { processingState } = useProcessing();
  const [tickets, setTickets] = useState(() => {
    // Load from server first, then merge with localStorage
    const savedTickets = localStorage.getItem('support_tickets_' + (user.email || user.userId));
    if (user.accountId) {
      fetch('/api/support-tickets/' + user.accountId)
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var serverTickets = (data.tickets && data.tickets.length > 0) ? data.tickets : [];
          var local = savedTickets ? JSON.parse(savedTickets) : [];
          
          // Merge: server tickets + any local-only tickets
          var merged = [...serverTickets];
          var localOnly = [];
          local.forEach(function(lt) {
            if (!merged.find(function(st) { return st.id === lt.id; })) {
              merged.push(lt);
              localOnly.push(lt);
            }
          });
          merged.sort(function(a, b) { return new Date(b.created) - new Date(a.created); });
          setTickets(merged);
          localStorage.setItem('support_tickets_' + (user.email || user.userId), JSON.stringify(merged));
          
          // Push any local-only tickets to server
          localOnly.forEach(function(ticket) {
            fetch('/api/support-tickets/' + user.accountId, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(ticket)
            }).catch(function(e) { console.log('Sync error:', e); });
          });
        })
        .catch(function(e) { console.log('Server ticket fetch error:', e); });
    }
    return savedTickets ? JSON.parse(savedTickets) : [];
  });

  const supportCategories = {
    general: {
      title: '💬 General Support',
      description: 'Getting started, features, and how things work',
      articles: [
        { id: 'getting-started', title: 'Getting started with VlogClip AI', content: {
          overview: 'VlogClip AI turns long YouTube videos into short, viral-ready clips with AI-powered captions, face tracking, emoji overlays, and more.',
          steps: [
            { title: '1. Create your account', description: 'Sign up with email or Google OAuth. Verify your email to start processing.', details: ['Click "Sign In" or "Start Free" on the homepage', 'Sign up with email/password or continue with Google', 'Check your inbox for the verification email and click the link', 'Google sign-in users are auto-verified'] },
            { title: '2. Generate your first clips', description: 'Paste a YouTube URL and let the AI do the work.', details: ['Go to the Generator page from your dashboard', 'Paste any public YouTube video URL', 'Choose your caption style, colour, aspect ratio, and emoji settings', 'Hit "Generate Viral Clips" and wait 2-5 minutes'] },
            { title: '3. Preview and download', description: 'Watch your clips, check engagement scores, and download.', details: ['Click any clip to preview it in the video player', 'Check the engagement score and AI-generated headline', 'Download individual clips or thumbnails', 'Clips are MP4 format, ready to upload anywhere'] }
          ],
          tips: ['Videos between 2-30 minutes give the best results', 'Clear speech produces better captions and AI analysis', 'Try different caption styles — Karaoke and Fight Hype are popular']
        }},
        { id: 'features-overview', title: 'Full features overview', content: {
          overview: 'Everything VlogClip AI offers across all plans.',
          steps: [
            { title: 'Caption Styles', description: '11 unique styles to make your clips stand out.', details: ['Minimal, Word Highlight, Hormozi Bold, CapCut Pop-In, Kinetic Zoom', 'Fight Hype, Karaoke (with dark shadow borders), Bold Pop, Explosion, Gradient Pop, Flash', '6 colour options: Yellow, Green, Cyan, Pink, Red, Orange', 'Free plan gets Minimal — Pro/Business unlock all 11'] },
            { title: 'Multilingual Support', description: '33 languages for UI, 31 for caption translation.', details: ['Language selector in the nav bar translates the entire UI', 'Caption translation converts subtitles to any of 31 languages', 'AI hooks auto-generate in the target language', 'Emoji overlays: 271 categories (including Gen Z slang) triggered by 2,160+ keywords in 31 languages', 'AI Help Assistant responds in your selected language'] },
            { title: 'Smart Processing', description: 'AI-powered clip selection and formatting.', details: ['Whisper transcription + GPT clip selection', 'Face detection: 3 modes — single face lock-on, softened crop (nearby people preserved), two-face midpoint', 'Podcast mode: auto-detects interview/podcast layout, scales full width with dark bars (no cropping)', 'AI viral hooks overlay on the first 3 seconds', 'Emoji overlays triggered by speech keywords', 'Engagement scoring ranks clip viral potential'] },
            { title: 'Aspect Ratios', description: 'Three formats for every platform.', details: ['9:16 — TikTok, Instagram Reels, YouTube Shorts', '4:5 — Instagram Feed, Facebook', '1:1 — Instagram, LinkedIn, X (Twitter)'] }
          ]
        }},
        { id: 'usage-limits', title: 'Plans and usage limits', content: {
          overview: 'Each plan has specific limits. Usage resets every 30 days from your account creation date.',
          plans: [
            { name: 'Free', limits: ['5 videos per month (15 clips)', '3 clips per video', '15-second max clip length', '720p resolution', 'Watermark on clips', '30-minute max video length'], notes: 'Great for trying out the platform' },
            { name: 'Creator Pro — \u00a39.99/mo', limits: ['20 videos per month', '5 clips per video', '30-second max clip length', '1080p HD', 'No watermark', 'Batch processing (3 URLs)', 'File upload (up to 2 GB)', 'Caption translation (31 languages)', 'AI hooks and emoji overlays', '5-hour clip storage', '60-minute max video length'], notes: 'For content creators who post regularly' },
            { name: 'Business — \u00a324.99/mo', limits: ['60 videos per month', '8 clips per video', '60-second max clip length', '1080p HD', 'No watermark', 'Batch processing (4 URLs)', 'File upload (up to 5 GB)', 'Custom branding', 'Priority queue', '10-hour clip storage', '90-minute max video length', 'Commercial usage rights'], notes: 'For agencies, brands, and high-volume creators' }
          ],
          faqs: [
            { q: 'What happens when I hit my limit?', a: 'You will see a message showing your remaining quota. Limits reset every 30 days.' },
            { q: 'Can I close the browser while processing?', a: 'Yes! Processing continues on the server. You will get an email when clips are ready.' },
            { q: 'What is podcast mode?', a: 'When VlogClip AI detects a 2-person interview or podcast layout, it automatically scales the full video width with dark bars top/bottom instead of cropping. All captions and overlays scale down to fit. No settings needed — it is automatic.' },
            { q: 'Does logout sync across tabs?', a: 'Yes. Logging out on one tab automatically logs out all other open tabs in the same browser.' }
          ]
        }},
        { id: 'ai-assistant', title: 'Using the AI Help Assistant', content: {
          overview: 'VlogClip AI has a built-in AI assistant that can answer your questions in real time.',
          steps: [
            { title: 'How to access it', description: 'Available everywhere in the app.', details: ['Desktop: click the purple chat bubble (bottom-right)', 'Mobile: tap the AI Help tab in the bottom nav', 'Documentation: click Ask AI Assistant in the sidebar'] },
            { title: 'What it can help with', description: 'It knows everything about VlogClip AI.', details: ['How to use any feature', 'Plan details and limits', 'Troubleshooting common issues', 'Caption style recommendations'] },
            { title: 'Daily limits', description: 'AI questions are rate-limited per plan.', details: ['Free: 5 questions/day', 'Pro: 20 questions/day', 'Business: 100 questions/day', 'Pre-written FAQ topics are always free'] }
          ]
        }}
      ]
    },
    technical: {
      title: '🔧 Technical Issues',
      description: 'Troubleshooting processing, downloads, and display issues',
      articles: [
        { id: 'processing-failed', title: 'Video failed or produced 0 clips', content: {
          overview: 'If processing fails or returns no clips, here are the most common causes.',
          commonCauses: ['Video is private, unlisted, or age-restricted', 'URL has playlist parameters — use direct link only', 'Video is too short (under 60 seconds)', 'YouTube is temporarily rate-limiting', 'Email not verified (free plan)'],
          solutions: [
            { issue: 'Private or restricted video', fixes: ['Use a public YouTube URL', 'Remove playlist/tracking params', 'Paste only: youtube.com/watch?v=...'] },
            { issue: 'Email not verified', fixes: ['Check inbox for verification email', 'Click Resend Email on the dashboard banner', 'Check spam/junk folders', 'Google sign-in is auto-verified'] },
            { issue: '0 clips generated', fixes: ['Video may lack clear speech for AI', 'Try a different video with more dialogue', 'Videos under 2 minutes may not have enough material'] }
          ],
          prevention: ['Use public videos with clear speech', 'Verify your email first', 'Videos 2-60 minutes work best']
        }},
        { id: 'processing-slow', title: 'Processing is slow', content: {
          overview: 'Processing times vary by video length and server load.',
          typicalTimes: ['Single video (under 15 min): 2-4 minutes', 'Single video (15-60 min): 4-8 minutes', 'Batch (3-4 videos): 4-10 minutes', 'Long videos (60-90 min): up to 12 minutes'],
          factorsAffecting: ['Video length', 'Number of clips', 'Server load', 'Caption style complexity'],
          troubleshooting: ['You can close the browser — processing continues on server', 'You will get an email when clips are ready', 'Refresh to check if clips appeared', 'If stuck 15+ minutes, try again']
        }},
        { id: 'download-issues', title: 'Download or expiry issues', content: {
          overview: 'Common download problems and clip expiry.',
          commonCauses: ['Clip storage expired', 'Browser blocking downloads', 'Mobile download restrictions'],
          solutions: [
            { issue: 'Clips expired', fixes: ['Pro: stored 5 hours, Business: 10 hours', 'Free: no storage — download immediately', 'Generate again if expired'] },
            { issue: 'Download not starting', fixes: ['Allow downloads from vlogclipai.com', 'Right-click and Save As', 'Check Downloads folder on mobile', 'Try a different browser'] }
          ]
        }},
        { id: 'caption-issues', title: 'Caption sync or display issues', content: {
          overview: 'Caption timing and display troubleshooting.',
          commonCauses: ['Unusual video encoding', 'Very fast speech', 'Non-standard container format'],
          solutions: [
            { issue: 'Captions early or late', fixes: ['Try a different caption style', 'Batch processing has more accurate sync', 'Clear speech at normal speed syncs best'] },
            { issue: 'No captions appearing', fixes: ['Make sure AI Captions is enabled', 'Video needs spoken audio (not music-only)', 'Check subtitle language selector for non-English videos'] }
          ]
        }}
      ]
    },
    api: {
      title: '🔌 API & Integration',
      description: 'API access and batch processing',
      articles: [
        { id: 'api-status', title: 'API access — coming soon', content: {
          overview: 'The REST API is in development for Business plan users.',
          steps: [
            { title: 'What to expect', description: 'Programmatic clip generation.', details: ['REST API for YouTube URL processing', 'Batch endpoint', 'Status polling and clip retrieval', 'JSON responses'] },
            { title: 'Register interest', description: 'Get early access.', details: ['Email support@vlogclipai.com to join the waitlist', 'Business subscribers get early access', 'No additional charge'] }
          ]
        }},
        { id: 'batch-guide', title: 'Batch processing guide', content: {
          overview: 'Process multiple YouTube videos at once (Pro and Business).',
          steps: [
            { title: 'How to use batch', description: 'Process 3-4 videos in parallel.', details: ['Go to Batch Processor from your dashboard', 'Paste up to 3 URLs (Pro) or 4 URLs (Business)', 'Choose caption style, colour, aspect ratio', 'Click Start Batch Processing', 'All videos process in parallel'] },
            { title: 'Tips', description: 'Get the most out of batch.', details: ['All videos use the same caption settings', 'Mix content types for variety', 'You can close the tab — email notification when done', 'Typical time: 4-10 minutes for a full batch'] }
          ]
        }}
      ]
    },
    billing: {
      title: '💳 Billing & Plans',
      description: 'Payments, upgrades, cancellations, and refunds',
      articles: [
        { id: 'how-to-upgrade', title: 'How to upgrade', content: {
          overview: 'Upgrade from your dashboard. Payments via Stripe.',
          steps: [
            { title: 'Upgrading', description: 'Premium features activate instantly.', details: ['Click Upgrade on your dashboard', 'Select Pro (\u00a39.99/mo) or Business (\u00a324.99/mo)', 'Secure Stripe checkout page', 'Visa, Mastercard, Amex accepted', 'Plan activates immediately'] },
            { title: 'What you get', description: 'Unlocks right away.', details: ['Pro: 1080p, no watermark, batch, translation, hooks, emoji', 'Business: + 8 clips/video, 60s clips, branding, 10hr storage'] },
            { title: 'Manage your subscription', description: 'Full billing control from the Billing page.', details: ['Click "Manage Subscription" to open the Stripe Customer Portal', 'Update payment method or change card details', 'View billing history with real Stripe invoices (date, amount, status)', 'Cancel or change plan — all self-service'] }
          ]
        }},
        { id: 'cancellation', title: 'How to cancel', content: {
          overview: 'Cancel any time through the Stripe Customer Portal or by email. No contracts.',
          steps: [
            { title: 'Cancel via Stripe Portal (recommended)', description: 'Self-service cancellation from your Billing page.', details: ['Go to your Billing page', 'Click "Manage Subscription" to open the Stripe Customer Portal', 'Click Cancel in the portal — takes effect at end of billing period', 'Full access until period ends, no further charges'] },
            { title: 'Cancel via email', description: 'Or email us.', details: ['Email support@vlogclipai.com', 'Active until end of billing period', 'Full access until period ends', 'No further charges'] },
            { title: 'Good to know', description: '', details: ['No contracts — cancel whenever', 'Re-subscribe any time', 'Account and settings preserved'] }
          ]
        }},
        { id: 'payment-info', title: 'Payment methods & security', content: {
          overview: 'All payments through Stripe. Card details never stored on our servers.',
          steps: [
            { title: 'Accepted cards', description: '', details: ['Visa', 'Mastercard', 'American Express', 'Prices in GBP — your bank handles conversion'] },
            { title: 'Security', description: '', details: ['Stripe handles all payments (PCI compliant)', 'Card details never touch our servers', 'Email receipt from Stripe after payment', 'Currency display: GBP, USD, EUR'] },
            { title: 'Billing history & invoices', description: 'View all your payment history.', details: ['Go to your Billing page to see real Stripe invoices', 'Each invoice shows date, amount, and payment status', 'Click "View" to open the full Stripe receipt', 'Update payment method via Stripe Customer Portal'] }
          ]
        }},
        { id: 'refunds', title: 'Refund policy', content: {
          overview: 'Refunds considered within 7 days of purchase.',
          steps: [
            { title: 'How to request', description: '', details: ['Email support@vlogclipai.com within 7 days', 'Include account email and reason', 'Reviewed within 2-3 business days', 'Refund to original payment method'] },
            { title: 'What qualifies', description: '', details: ['Service not working as described', 'Technical issues preventing usage', 'Billing errors or duplicate charges'] }
          ]
        }}
      ]
    }
  };

  const generateTicketId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `TKT-${timestamp}-${random}`;
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const ticketId = generateTicketId();
      
      // Create ticket data
      const newTicket = {
        id: ticketId,
        subject: ticketForm.subject,
        message: ticketForm.message,
        category: ticketForm.category,
        priority: ticketForm.priority,
        status: 'open',
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        messages: 1,
        userEmail: user.email || user.userId
      };

      // Send email via API
      try {
        const response = await fetch('/api/send-support-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ticketId: ticketId,
            userEmail: user.email || user.userId,
            replyToEmail: ticketForm.userEmail,
            firstName: ticketForm.firstName,
            lastName: ticketForm.lastName,
            userName: user.username || user.displayName || user.email?.split('@')[0] || 'Unknown User',
            userPlan: user.plan || 'Free',
            category: ticketForm.category,
            priority: ticketForm.priority,
            subject: ticketForm.subject,
            message: ticketForm.message,
            device: navigator.userAgent.split(')')[0] + ')' || 'Unknown'
          })
        });

        const result = await response.json();
        
        if (response.ok) {
          // Update tickets state
          const updatedTickets = [newTicket, ...tickets];
          setTickets(updatedTickets);
          
          // Save to localStorage
          localStorage.setItem('support_tickets_' + (user.email || user.userId), JSON.stringify(updatedTickets));
          // Also save to server (awaited for cross-device sync)
          if (user.accountId) {
            try {
              await fetch('/api/support-tickets/' + user.accountId, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newTicket)
              });
              console.log('✅ Ticket saved to server');
            } catch(e) { console.log('Server ticket save error:', e); }
          }
          
          // Reset form
          setTicketForm({ firstName: '', lastName: '', subject: '', message: '', priority: 'normal', category: 'general', userEmail: '' });
          setIsSubmitting(false);
          
          // Show success message
          alert(`Support ticket ${ticketId} submitted successfully! Your message has been sent to our support team at vlogclipai@gmail.com.`);
        } else {
          throw new Error(result.message || 'Failed to send email');
        }
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
        
        // Fallback to mailto link
        const emailContent = `
Support Ticket: ${ticketId}

User Information:
Account Name: ${ticketForm.firstName && ticketForm.lastName ? `${ticketForm.firstName} ${ticketForm.lastName}` : user.username || user.displayName || user.email?.split('@')[0] || 'Unknown User'}
First Name: ${ticketForm.firstName || 'Not provided'}
Last Name: ${ticketForm.lastName || 'Not provided'}
Dashboard Email: ${user.email || user.userId || 'Unknown'}
Reply-To Email: ${ticketForm.userEmail}
Plan: ${user.plan || 'Free'}
Device: ${navigator.userAgent.split(')')[0] + ')' || 'Unknown'}

Ticket Details:
Category: ${ticketForm.category}
Priority: ${ticketForm.priority}
Subject: ${ticketForm.subject}

Message:
${ticketForm.message}

---
Submitted: ${new Date().toLocaleString()}
Dashboard: ${window.location.origin}
User ID: ${user.userId || user.email || 'N/A'}

IMPORTANT: Please reply to ${ticketForm.userEmail} when responding to this support ticket.
        `;

        const mailtoLink = `mailto:vlogclipai@gmail.com?subject=Support Ticket - ${user.username || user.displayName || ticketForm.userEmail.split('@')[0]} (${user.plan || 'Free'} Plan) - ${ticketForm.subject}&body=${encodeURIComponent(emailContent)}`;
        
        // Update tickets state even if email fails
        const updatedTickets = [newTicket, ...tickets];
        setTickets(updatedTickets);
        localStorage.setItem('support_tickets_' + (user.email || user.userId), JSON.stringify(updatedTickets));
        
        // Save to server for cross-device sync
        if (user.accountId) {
          try {
            await fetch('/api/support-tickets/' + user.accountId, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(newTicket)
            });
            console.log('✅ Ticket saved to server (fallback path)');
          } catch(e) { console.log('Server ticket save error (fallback):', e); }
        }
        
        // Reset form
        setTicketForm({ firstName: '', lastName: '', subject: '', message: '', priority: 'normal', category: 'general', userEmail: '' });
        setIsSubmitting(false);
        
        // Open email client as fallback
        window.open(mailtoLink, '_blank');
        
        alert(`Support ticket ${ticketId} created! Email sending failed, but we've opened your email client. Please send the email manually.`);
      }
      
    } catch (error) {
      console.error('Error submitting ticket:', error);
      setIsSubmitting(false);
      alert('There was an error submitting your ticket. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return { bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)', text: '#3b82f6' };
      case 'in-progress': return { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', text: '#f59e0b' };
      case 'answered': return { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', text: '#10b981' };
      case 'closed': return { bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.3)', text: '#6b7280' };
      default: return { bg: 'rgba(255, 255, 255, 0.05)', border: 'rgba(255, 255, 255, 0.1)', text: '#ffffff' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return '#10b981';
      case 'normal': return '#6366f1';
      case 'high': return '#f59e0b';
      case 'urgent': return '#ef4444';
      default: return '#6366f1';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Less than 1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  // Show AI Insights if requested
  if (showAIInsights) {
    return <AIInsights onBack={() => setShowAIInsights(false)} />;
  }

  return (
    <div className="user-dashboard">
        <div style={{padding:'0 0 8px 0'}}><button className="dash-back-btn" onClick={() => { window.history.pushState({section:'dashboard'}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate', {state:{section:'dashboard'}})); }}>🏠 ← Dashboard</button></div>
      <div className="dashboard-header">
        <div className="user-info">
          <h1>💬 Support Center</h1>
          <p className="user-email">Get help from our expert team</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ 
            padding: '8px 16px', 
            background: 'rgba(16, 185, 129, 0.1)', 
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '8px',
            color: '#10b981',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            🟢 Support Online
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr', gap: window.innerWidth <= 768 ? '12px' : '24px', marginBottom: '32px', position: 'relative', zIndex: 1 }}>
        {/* Quick Stats */}
        <div className="dashboard-card">
          <h2>📊 Support Stats</h2>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="stat-item">
              <span className="stat-number">&lt; 4h</span>
              <span className="stat-label">Avg Response</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">97%</span>
              <span className="stat-label">Satisfaction</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{tickets.length}</span>
              <span className="stat-label">Your Tickets</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Availability</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card">
          <h2>⚡ Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => setShowAIInsights(true)}
              className="action-btn primary"
              style={{ 
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              <span className="btn-icon">🧠</span>
              <span className="btn-title">Act on Insight</span>
            </button>
            <button
              onClick={() => {
                const el = document.querySelector('h2#submit-ticket') || document.querySelector('form');
                if (el) el.scrollIntoView({behavior:'smooth'});
                else window.scrollTo({top: 0, behavior:'smooth'});
              }}
              className="action-btn primary"
              style={{ 
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              <span className="btn-icon">📧</span>
              <span className="btn-title">Submit a Ticket</span>
            </button>
            <div style={{ 
              textAlign: 'center', 
              color: '#94a3b8', 
              fontSize: '14px',
              padding: '12px',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>📧 vlogclipai@gmail.com</p>
              <p style={{ margin: '0', fontSize: '12px' }}>Average response time: 4-6 hours</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '300px 1fr', gap: window.innerWidth <= 768 ? '12px' : '24px', position: 'relative', zIndex: 1 }}>
        {/* Categories Sidebar */}
        <div className="dashboard-card">
          <h2>📋 Categories</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Object.entries(supportCategories).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                style={{
                  padding: '12px 16px',
                  background: selectedCategory === key ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
                  border: selectedCategory === key ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid transparent',
                  borderRadius: '8px',
                  color: selectedCategory === key ? '#ffffff' : '#94a3b8',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: selectedCategory === key ? '600' : '500'
                }}
              >
                {category.title}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Knowledge Base */}
          <div className="dashboard-card">
            {!selectedArticle ? (
              <>
                <h2>{supportCategories[selectedCategory].title}</h2>
                <p style={{ color: '#94a3b8', marginBottom: '20px' }}>
                  {supportCategories[selectedCategory].description}
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {supportCategories[selectedCategory].articles.map((article, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedArticle(article)}
                      style={{
                        padding: '16px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <h4 style={{ color: '#ffffff', margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>
                            {article.title}
                          </h4>
                          <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#94a3b8' }}>
                            
                            
                          </div>
                        </div>
                        <span style={{ color: '#6366f1', fontSize: '18px' }}>→</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ minHeight: '500px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <button
                    onClick={() => setSelectedArticle(null)}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      color: '#ffffff',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    ← Back to {supportCategories[selectedCategory].title}
                  </button>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#94a3b8' }}>
                    
                    
                  </div>
                </div>
                
                <h2 style={{ marginBottom: '16px' }}>{selectedArticle.title}</h2>
                
                <div style={{ color: '#e2e8f0', lineHeight: '1.6' }}>
                  <p style={{ marginBottom: '24px', fontSize: '16px', color: '#94a3b8' }}>
                    {selectedArticle.content.overview}
                  </p>
                  
                  {/* Render content based on article structure */}
                  {selectedArticle.content.steps && (
                    <div style={{ marginBottom: '32px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '16px' }}>Getting Started</h3>
                      {selectedArticle.content.steps.map((step, index) => (
                        <div key={index} style={{ marginBottom: '24px', padding: '16px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px' }}>
                          <h4 style={{ color: '#6366f1', marginBottom: '8px' }}>{step.title}</h4>
                          <p style={{ marginBottom: '12px', color: '#94a3b8' }}>{step.description}</p>
                          <ul style={{ paddingLeft: '20px', color: '#e2e8f0' }}>
                            {step.details.map((detail, i) => (
                              <li key={i} style={{ marginBottom: '4px' }}>{detail}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {selectedArticle.content.tips && (
                    <div style={{ marginBottom: '32px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '16px' }}>💡 Pro Tips</h3>
                      <ul style={{ paddingLeft: '20px', color: '#e2e8f0' }}>
                        {selectedArticle.content.tips.map((tip, index) => (
                          <li key={index} style={{ marginBottom: '8px' }}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedArticle.content.commonCauses && (
                    <div style={{ marginBottom: '28px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>Common Causes</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {selectedArticle.content.commonCauses.map((cause, i) => (
                          <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '8px 12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', borderRadius: '8px' }}>
                            <span style={{ color: '#f87171', fontSize: '14px', flexShrink: 0 }}>⚠</span>
                            <span style={{ color: '#e2e8f0', fontSize: '13px', lineHeight: '1.5' }}>{cause}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedArticle.content.solutions && (
                    <div style={{ marginBottom: '28px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>Solutions</h3>
                      {selectedArticle.content.solutions.map((sol, i) => (
                        <div key={i} style={{ marginBottom: '16px', padding: '16px', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: '10px' }}>
                          <h4 style={{ color: '#34d399', fontSize: '14px', fontWeight: '700', marginBottom: '10px' }}>{sol.issue}</h4>
                          <ul style={{ paddingLeft: '20px', margin: 0 }}>
                            {sol.fixes.map((fix, j) => (
                              <li key={j} style={{ color: '#cbd5e1', fontSize: '13px', marginBottom: '6px', lineHeight: '1.5' }}>{fix}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedArticle.content.typicalTimes && (
                    <div style={{ marginBottom: '28px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>Typical Processing Times</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {selectedArticle.content.typicalTimes.map((time, i) => (
                          <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '10px 12px', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.12)', borderRadius: '8px' }}>
                            <span style={{ color: '#818cf8', fontSize: '14px' }}>⏱</span>
                            <span style={{ color: '#e2e8f0', fontSize: '13px' }}>{time}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedArticle.content.factorsAffecting && (
                    <div style={{ marginBottom: '28px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>Factors That Affect Speed</h3>
                      <ul style={{ paddingLeft: '20px', color: '#cbd5e1' }}>
                        {selectedArticle.content.factorsAffecting.map((factor, i) => (
                          <li key={i} style={{ marginBottom: '6px', fontSize: '13px', lineHeight: '1.5' }}>{factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedArticle.content.troubleshooting && (
                    <div style={{ marginBottom: '28px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>What To Try</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {selectedArticle.content.troubleshooting.map((item, i) => (
                          <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '8px 12px', background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.12)', borderRadius: '8px' }}>
                            <span style={{ color: '#a78bfa', fontSize: '13px', fontWeight: '700', flexShrink: 0 }}>{i + 1}.</span>
                            <span style={{ color: '#e2e8f0', fontSize: '13px', lineHeight: '1.5' }}>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedArticle.content.prevention && (
                    <div style={{ marginBottom: '28px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>Prevention</h3>
                      <ul style={{ paddingLeft: '20px', color: '#cbd5e1' }}>
                        {selectedArticle.content.prevention.map((item, i) => (
                          <li key={i} style={{ marginBottom: '6px', fontSize: '13px', lineHeight: '1.5' }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedArticle.content.plans && (
                    <div style={{ marginBottom: '28px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>Plan Details</h3>
                      {selectedArticle.content.plans.map((plan, i) => (
                        <div key={i} style={{ marginBottom: '12px', padding: '16px', background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)', borderRadius: '10px' }}>
                          <h4 style={{ color: '#a78bfa', fontSize: '15px', fontWeight: '700', marginBottom: '10px' }}>{plan.name}</h4>
                          <ul style={{ paddingLeft: '20px', margin: '0 0 8px 0' }}>
                            {plan.limits.map((limit, j) => (
                              <li key={j} style={{ color: '#e2e8f0', fontSize: '13px', marginBottom: '4px' }}>{limit}</li>
                            ))}
                          </ul>
                          {plan.notes && <p style={{ color: '#64748b', fontSize: '12px', margin: 0, fontStyle: 'italic' }}>{plan.notes}</p>}
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedArticle.content.faqs && (
                    <div style={{ marginBottom: '28px' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '12px' }}>FAQ</h3>
                      {selectedArticle.content.faqs.map((faq, i) => (
                        <div key={i} style={{ marginBottom: '12px', padding: '14px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.12)', borderRadius: '10px' }}>
                          <div style={{ color: '#fbbf24', fontSize: '13px', fontWeight: '700', marginBottom: '6px' }}>Q: {faq.q}</div>
                          <div style={{ color: '#cbd5e1', fontSize: '13px', lineHeight: '1.5' }}>A: {faq.a}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Submit Ticket */}
          <div className="dashboard-card">
            <h2 id="submit-ticket">🎫 Submit New Ticket</h2>
            <form onSubmit={handleTicketSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#e2e8f0', fontWeight: '600' }}>
                  Your Email Address (for replies)
                </label>
                <input
                  type="email"
                  value={ticketForm.userEmail}
                  onChange={(e) => setTicketForm(prev => ({ ...prev, userEmail: e.target.value }))}
                  placeholder="Enter your email address where you want to receive replies"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#e2e8f0', fontWeight: '600' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={ticketForm.firstName}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Enter your first name"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#e2e8f0', fontWeight: '600' }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={ticketForm.lastName}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Enter your last name"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#e2e8f0', fontWeight: '600' }}>
                    Category
                  </label>
                  <select
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, category: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="general">General Support</option>
                    <option value="technical">Technical Issues</option>
                    <option value="api">API & Integration</option>
                    <option value="billing">Billing & Plans</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#e2e8f0', fontWeight: '600' }}>
                    Priority
                  </label>
                  <select
                    value={ticketForm.priority}
                    onChange={(e) => setTicketForm(prev => ({ ...prev, priority: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#e2e8f0', fontWeight: '600' }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={ticketForm.subject}
                  onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Brief description of your issue"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#e2e8f0', fontWeight: '600' }}>
                  Message
                </label>
                <textarea
                  value={ticketForm.message}
                  onChange={(e) => setTicketForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Please provide detailed information about your issue..."
                  required
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '12px 24px',
                  background: isSubmitting ? 'rgba(99, 102, 241, 0.3)' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ffffff',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
              >
                {isSubmitting ? '🔄 Submitting...' : '📤 Submit Ticket'}
              </button>
            </form>
          </div>

          {/* Your Tickets */}
          <div className="dashboard-card">
            <h2>🎫 Your Support Tickets</h2>
            {tickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎫</div>
                <p>No support tickets yet</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {tickets.map((ticket) => {
                  const statusColors = getStatusColor(ticket.status);
                  return (
                    <div
                      key={ticket.id}
                      onClick={() => setSelectedTicket(ticket)}
                      style={{
                        padding: '16px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                            <span style={{ color: '#6366f1', fontWeight: '700', fontSize: '14px' }}>
                              #{ticket.id}
                            </span>
                            <span
                              style={{
                                padding: '4px 8px',
                                background: statusColors.bg,
                                border: `1px solid ${statusColors.border}`,
                                borderRadius: '12px',
                                color: statusColors.text,
                                fontSize: '11px',
                                fontWeight: '600',
                                textTransform: 'uppercase'
                              }}
                            >
                              {ticket.status.replace('-', ' ')}
                            </span>
                            <div
                              style={{
                                width: '8px',
                                height: '8px',
                                background: getPriorityColor(ticket.priority),
                                borderRadius: '50%'
                              }}
                              title={`${ticket.priority} priority`}
                            />
                          </div>
                          <h4 style={{ color: '#ffffff', margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>
                            {ticket.subject}
                          </h4>
                          <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#94a3b8' }}>
                            <span>Created: {formatTimeAgo(ticket.created)}</span>
                            <span>Updated: {formatTimeAgo(ticket.updated)}</span>
                            <span>{ticket.messages} messages</span>
                          </div>
                        </div>
                        <span style={{ color: '#6366f1', fontSize: '18px' }}>→</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            background: '#1e293b',
            borderRadius: '16px',
            width: '90vw',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h2 style={{ color: '#ffffff', margin: 0, fontSize: '20px', fontWeight: '700' }}>
                    Support Ticket #{selectedTicket.id}
                  </h2>
                  <span
                    style={{
                      padding: '4px 12px',
                      background: getStatusColor(selectedTicket.status).bg,
                      border: `1px solid ${getStatusColor(selectedTicket.status).border}`,
                      borderRadius: '16px',
                      color: getStatusColor(selectedTicket.status).text,
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}
                  >
                    {selectedTicket.status.replace('-', ' ')}
                  </span>
                </div>
                <h3 style={{ color: '#e2e8f0', margin: 0, fontSize: '18px', fontWeight: '600' }}>
                  {selectedTicket.subject}
                </h3>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = '#ffffff';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#94a3b8';
                }}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div style={{
              padding: '24px',
              maxHeight: 'calc(80vh - 120px)',
              overflowY: 'auto'
            }}>
              {/* Ticket Details */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                    Category
                  </label>
                  <p style={{ color: '#ffffff', margin: '4px 0 0 0', fontSize: '14px', fontWeight: '600' }}>
                    {selectedTicket.category.charAt(0).toUpperCase() + selectedTicket.category.slice(1)}
                  </p>
                </div>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                    Priority
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        background: getPriorityColor(selectedTicket.priority),
                        borderRadius: '50%'
                      }}
                    />
                    <p style={{ color: '#ffffff', margin: 0, fontSize: '14px', fontWeight: '600' }}>
                      {selectedTicket.priority.charAt(0).toUpperCase() + selectedTicket.priority.slice(1)}
                    </p>
                  </div>
                </div>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                    Created
                  </label>
                  <p style={{ color: '#ffffff', margin: '4px 0 0 0', fontSize: '14px', fontWeight: '600' }}>
                    {new Date(selectedTicket.created).toLocaleDateString()} at {new Date(selectedTicket.created).toLocaleTimeString()}
                  </p>
                </div>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>
                    Last Updated
                  </label>
                  <p style={{ color: '#ffffff', margin: '4px 0 0 0', fontSize: '14px', fontWeight: '600' }}>
                    {formatTimeAgo(selectedTicket.updated)}
                  </p>
                </div>
              </div>

              {/* Original Message */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px'
                  }}>
                    👤
                  </div>
                  <div>
                    <p style={{ color: '#ffffff', margin: 0, fontSize: '14px', fontWeight: '600' }}>
                      {user.username || user.displayName || user.email || 'You'}
                    </p>
                    <p style={{ color: '#94a3b8', margin: 0, fontSize: '12px' }}>
                      {new Date(selectedTicket.created).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div style={{
                  color: '#e2e8f0',
                  lineHeight: '1.6',
                  fontSize: '15px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedTicket.message}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <button
                  onClick={async () => {
                    // Prompt user for their personal email address
                    const userPersonalEmail = prompt(
                      'Please enter your personal email address where you want to receive follow-up responses:',
                      ''
                    );
                    
                    if (!userPersonalEmail || !userPersonalEmail.includes('@')) {
                      alert('Please enter a valid email address to receive follow-up responses.');
                      return;
                    }

                    // Prompt for follow-up message
                    const followUpMessage = prompt(
                      'Please enter your follow-up message:',
                      'I have additional information regarding this ticket...'
                    );

                    if (!followUpMessage) {
                      alert('Please enter a follow-up message.');
                      return;
                    }

                    // Generate new follow-up ticket ID
                    const followUpTicketId = `FOLLOWUP-${selectedTicket.id}-${Date.now().toString().slice(-6)}`;

                    // Create comprehensive email with all original ticket details
                    const ticketData = {
                      ticketId: followUpTicketId,
                      userEmail: user.email || user.userId,
                      replyToEmail: userPersonalEmail,
                      userName: user.username || user.displayName || user.email?.split('@')[0] || 'Unknown User',
                      userPlan: user.plan || 'Free',
                      category: selectedTicket.category || 'follow-up',
                      priority: selectedTicket.priority || 'normal',
                      subject: `Follow-up: ${selectedTicket.subject}`,
                      message: `${followUpMessage}

---
ORIGINAL TICKET DETAILS:
Ticket ID: ${selectedTicket.id}
Original Subject: ${selectedTicket.subject}
Original Category: ${selectedTicket.category}
Original Priority: ${selectedTicket.priority}
Created: ${new Date(selectedTicket.created).toLocaleString()}
Original Message:
${selectedTicket.message}

Status: Follow-up submitted
Dashboard Account: ${user.username || user.email || 'Unknown'}`,
                      device: navigator.userAgent.split(')')[0] + ')' || 'Unknown'
                    };

                    try {
                      // Send via API to ensure both emails are sent
                      const response = await fetch('/api/send-support-email', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(ticketData)
                      });

                      const result = await response.json();

                      if (result.success) {
                        alert(`Follow-up ticket ${followUpTicketId} submitted successfully!\n\nConfirmation email sent to: ${userPersonalEmail}\nSupport team notified at: vlogclipai@gmail.com`);
                        
                        // Add follow-up to local tickets list
                        const newFollowUp = {
                          id: followUpTicketId,
                          subject: ticketData.subject,
                          message: followUpMessage,
                          category: ticketData.category,
                          priority: ticketData.priority,
                          created: Date.now(),
                          status: 'submitted',
                          replyToEmail: userPersonalEmail,
                          originalTicket: selectedTicket.id
                        };
                        
                        const updatedTickets = [...tickets, newFollowUp];
                        setTickets(updatedTickets);
                        localStorage.setItem('support_tickets_' + (user.email || user.userId), JSON.stringify(updatedTickets));
                      } else {
                        throw new Error(result.message || 'Failed to send follow-up');
                      }
                    } catch (error) {
                      console.error('Follow-up submission failed:', error);
                      
                      // Fallback to mailto link with full details
                      const fallbackEmailContent = `Follow-up Message:
${followUpMessage}

---
ORIGINAL TICKET DETAILS:
Ticket ID: ${selectedTicket.id}
Original Subject: ${selectedTicket.subject}
Original Category: ${selectedTicket.category}
Original Priority: ${selectedTicket.priority}
Created: ${new Date(selectedTicket.created).toLocaleString()}
Original Message:
${selectedTicket.message}

Personal Email for Reply: ${userPersonalEmail}
Account: ${user.username || user.email || 'Unknown'}

IMPORTANT: Please reply to ${userPersonalEmail} when responding to this follow-up.`;

                      const mailtoLink = `mailto:vlogclipai@gmail.com?subject=Follow-up: ${selectedTicket.subject}&body=${encodeURIComponent(fallbackEmailContent)}`;
                      window.open(mailtoLink, '_blank');
                      
                      alert('API submission failed - opened email client as fallback. Please send the email manually.');
                    }
                  }}
                  style={{
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                >
                  📧 Follow Up
                </button>
                <button
                  onClick={() => setSelectedTicket(null)}
                  style={{
                    padding: '12px 20px',
                    background: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPanel;